import type { ClipPayload } from '@vp/core';
import type { ObjectRecord } from '@vp/shared-types';

/**
 * Large things copied objects depend on — the data sets of tables and charts later (docs/TZ-DATA.md) —
 * do not travel inside the clipboard (it is text in the system clipboard and a key of the profile, a few
 * MB at most). They travel as references to the canvas they were copied from:
 *
 * - pasted into the same canvas, the objects keep pointing at the same thing;
 * - pasted into another canvas of the same profile, the thing is copied there and the objects are
 *   pointed at the copy;
 * - pasted where the source is out of reach (another profile, another browser), nothing is copied and
 *   the objects show that their data is not available.
 *
 * A kind of reference is one entry here (`registerClipPart`); the clipboard itself does not change.
 */
export interface ClipRef {
  /** Kind of the reference (the part that resolves it). */
  part: string;
  /** Id of the thing in the source canvas. */
  id: string;
  /** Size in bytes when known: the paste may ask before copying something big. */
  size?: number;
}

/** Where a copy was made: a reference is reachable only from the same profile. */
export interface ClipSource {
  profile: string | null;
  canvas: string;
}

export interface ClipPart {
  part: string;
  /** References the copied objects hold. */
  collect(objects: ObjectRecord[]): ClipRef[];
  /** Copy the thing into another canvas; its new id, or null when it is gone. */
  copy(ref: ClipRef, from: ClipSource, toCanvas: string): Promise<string | null>;
  /** Point the objects at new ids (`map`: old id → new id, or null — not available). */
  rewrite(objects: ObjectRecord[], map: Map<string, string | null>): ObjectRecord[];
}

export interface ClipRefsPayload extends ClipPayload {
  source?: ClipSource;
  refs?: ClipRef[];
}

const parts = new Map<string, ClipPart>();

export function registerClipPart(p: ClipPart): void {
  if (parts.has(p.part)) throw new Error(`clipboard part ${p.part} is registered twice`);
  parts.set(p.part, p);
}

/** Tests only. */
export function resetClipPartsForTests(): void {
  parts.clear();
}

/** At copy: the references of the copied objects and where they come from. */
export function withRefs<P extends ClipPayload>(p: P, source: ClipSource): P & ClipRefsPayload {
  const refs: ClipRef[] = [];
  for (const part of parts.values()) {
    const seen = new Set<string>();
    for (const r of part.collect(p.objects)) {
      if (seen.has(r.id)) continue;
      seen.add(r.id);
      refs.push({ ...r, part: part.part });
    }
  }
  return refs.length ? { ...p, source, refs } : p;
}

/**
 * At paste into `target`: copy what the objects reference when it comes from another canvas.
 * `confirmBig` is asked once for everything bigger than `bigBytes` together; false — the objects come
 * without their data.
 */
export async function resolveRefs<P extends ClipRefsPayload>(
  p: P,
  target: { profile: string | null; canvas: string },
  opts: { bigBytes?: number; confirmBig?: (bytes: number) => Promise<boolean> } = {},
): Promise<P> {
  if (!p.refs?.length) return p;
  const src = p.source;
  // the same canvas: the objects keep pointing at what is already there
  if (src && src.canvas === target.canvas && src.profile === target.profile) return p;
  const reachable = !!src && src.profile === target.profile;
  let allow = reachable;
  if (reachable && opts.confirmBig) {
    const total = p.refs.reduce((n, r) => n + (r.size ?? 0), 0);
    if (total > (opts.bigBytes ?? 20 * 1024 * 1024)) allow = await opts.confirmBig(total);
  }
  let objects = p.objects;
  for (const part of parts.values()) {
    const own = p.refs.filter((r) => r.part === part.part);
    if (!own.length) continue;
    const map = new Map<string, string | null>();
    for (const r of own) map.set(r.id, allow && src ? await part.copy(r, src, target.canvas).catch(() => null) : null);
    objects = part.rewrite(objects, map);
  }
  return { ...p, objects };
}
